import { Component, Input, OnInit } from '@angular/core';
import { MenuItem } from '../../../core/interfaces/menu-item.interface';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';

@Component({
  selector: 'app-menu-item',
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatBadgeModule
  ],
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss'
})
export class MenuItemComponent implements OnInit {

  /**
   * Labels of the parents the user has expanded. Every page renders its own layout, so the menu is
   * rebuilt on each navigation — the open state lives here to survive picking a sub-item. Plain UI
   * state, so raw JSON in sessionStorage like the table filters.
   */
  private static readonly EXPANDED_KEY = 'sidenavExpanded';

  @Input({ required: true }) item!: MenuItem;

  constructor(
    private router: Router
  ) { } // Router for isActive check is often injected here

  ngOnInit(): void {
    if (this.item.isParent) {
      // Open the group the current page lives in, so the active child is visible on arrival.
      this.item.expanded = this.readExpanded().includes(this.item.label) || this.hasActiveChild();
    }
  }

  /** True when a parent's child matches the current URL — lets a collapsed parent still show where you are. */
  hasActiveChild(): boolean {
    if (!this.item.isParent || !this.item.children) {
      return false;
    }
    const path = this.currentPath();
    return this.item.children.some(child => child.route === path || !!child.activeRoutes?.includes(path));
  }

  /** Chevron click: toggle the sub-menu without letting the click reach the parent's link. */
  toggleExpand(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.item.expanded = !this.item.expanded;

    const others = this.readExpanded().filter(label => label !== this.item.label);
    const expanded = this.item.expanded ? [...others, this.item.label] : others;
    sessionStorage.setItem(MenuItemComponent.EXPANDED_KEY, JSON.stringify(expanded));
  }

  private readExpanded(): string[] {
    try {
      const stored = JSON.parse(sessionStorage.getItem(MenuItemComponent.EXPANDED_KEY) ?? '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }

  // Placeholder for a real isActive check (requires injecting Router)
  isActive(route: string): boolean {
    // A parent only groups its children; the active child carries the highlight.
    if (this.item.isParent) {
      return false;
    }

    if (this.item.activeRoutes) {
      const path = this.currentPath();
      return path === route || this.item.activeRoutes.includes(path);
    }

    // In a real app, use the Router to check if the current URL matches the route
    return this.router.url === route;
    // return false;
  }

  /** The current URL without its query string or fragment. */
  private currentPath(): string {
    return this.router.url.split(/[?#]/)[0];
  }

}
